import { ref, computed, readonly } from 'vue'
import { defineStore } from 'pinia'
import { supabase } from '@/lib/supabase'
import { authService } from '@/services/authService'
import { mfaService } from '@/services/mfaService'
import { userService } from '@/services/userService'
import type { User, UserOnboardingPayload, UserProfileUpdate } from '@/types'

export class MfaRequiredError extends Error {
  readonly code = 'MFA_REQUIRED'
  constructor(public readonly factorId: string) {
    super('MFA required')
  }
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null)
  const user = ref<User | null>(null)
  const pendingMfaFactorId = ref<string | null>(null)
  const isCreatingProfile = ref(false)

  // Tracks the in-flight ensureProfileLoaded() promise so concurrent callers
  // (router guard, AuthCallbackPage, onAuthStateChange listener) all await
  // the same resolution instead of racing each other.
  let profileLoadPromise: Promise<User | null> | null = null

  const isAuthenticated = computed(() => !!token.value)
  const needsOnboarding = computed(
    () => !!user.value && !user.value.onboarding_completed_at,
  )

  async function _hydrateUserFromSession(accessToken: string): Promise<User | null> {
    try {
      const me = await authService.me(accessToken)
      user.value = me
      return me
    } catch {
      // Profile doesn't exist yet (first sign-in after email confirmation).
      isCreatingProfile.value = true
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return null
        try {
          await authService.createProfileFromSession(session)
        } catch {
          // Ignore: 409 = profile already exists, or transient failure.
          // GET /me below succeeds if the profile is present.
        }
        const me = await authService.me(session.access_token)
        user.value = me
        return me
      } catch {
        return null
      } finally {
        isCreatingProfile.value = false
      }
    }
  }

  /**
   * Idempotent: ensures `user` is populated before resolving. Concurrent
   * callers (router guard + onAuthStateChange + AuthCallbackPage) share the
   * same in-flight promise, so we never fire parallel POST /users/ requests.
   */
  async function ensureProfileLoaded(): Promise<User | null> {
    if (user.value) return user.value
    if (profileLoadPromise) return profileLoadPromise

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return null

    profileLoadPromise = _hydrateUserFromSession(session.access_token).finally(() => {
      profileLoadPromise = null
    })
    return profileLoadPromise
  }

  supabase.auth.onAuthStateChange(async (event, session) => {
    token.value = session?.access_token ?? null
    if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && !user.value) {
      await ensureProfileLoaded()
    } else if (!session) {
      user.value = null
      pendingMfaFactorId.value = null
    }
  })

  // Hydrate from persisted session on app start
  supabase.auth.getSession().then(({ data: { session } }) => {
    token.value = session?.access_token ?? null
    if (session) ensureProfileLoaded().catch(() => {})
  })

  async function login(email: string, password: string) {
    await authService.login(email, password)

    // Check if MFA upgrade is required
    const aal = await mfaService.getAssuranceLevel()
    if (aal.currentLevel === 'aal1' && aal.nextLevel === 'aal2') {
      const factor = await mfaService.getVerifiedFactor()
      if (factor) {
        pendingMfaFactorId.value = factor.id
        throw new MfaRequiredError(factor.id)
      }
    }
    // onAuthStateChange handles token and user hydration
  }

  function clearPendingMfa() {
    pendingMfaFactorId.value = null
  }

  async function fetchMe(accessToken?: string) {
    user.value = await authService.me(accessToken)
  }

  async function updateProfile(data: UserProfileUpdate) {
    user.value = await userService.updateProfile(data)
  }

  async function completeOnboarding(payload: UserOnboardingPayload) {
    user.value = await userService.completeOnboarding(payload)
  }

  async function changePassword(payload: { current_password: string; new_password: string }) {
    const email = user.value?.email
    if (!email) throw new Error('Not authenticated')
    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password: payload.current_password,
    })
    if (authError) {
      const err = new Error('wrong_current_password') as Error & { code: string }
      err.code = 'wrong_current'
      throw err
    }
    const { error } = await supabase.auth.updateUser({ password: payload.new_password })
    if (error) throw error
  }

async function logout() {
    await authService.signOut()
    // onAuthStateChange clears token and user
  }

  return {
    token: readonly(token),
    user: readonly(user),
    pendingMfaFactorId: readonly(pendingMfaFactorId),
    isAuthenticated,
    needsOnboarding,
    isCreatingProfile: readonly(isCreatingProfile),
    login,
    clearPendingMfa,
    fetchMe,
    ensureProfileLoaded,
    updateProfile,
    completeOnboarding,
    changePassword,
    logout,
  }
})
